import { BulkAction } from '@/enums/BulkAction';
import { SortOrder } from '@/enums/SortOrder';
import qs from 'qs';
import { Module, ActionTree, MutationTree, GetterTree } from 'vuex';

const initialState: CollectState = {
  all: [],
};

const getters: GetterTree<CollectState, RootState> = {
  all(state: CollectState): Collect[] {
    return state.all;
  },
};

const mutations: MutationTree<CollectState> = {
  RESET(state: CollectState) {
    state.all = [];
  },
  ADD_MODEL(state: CollectState, payload: Collect) {
    state.all.push(payload);
  },
  ADD_MODELS(state: CollectState, payload: Collect[]) {
    state.all = [...state.all, ...payload];
  },
  SET_MODELS(state: CollectState, payload: Collect[]) {
    state.all = payload;
  },
  UPDATE_MODEL(state: CollectState, payload: Collect) {
    const index = state.all.findIndex((model: Collect) => model.id === payload.id);

    if (index === -1) {
      state.all.push(payload);
    } else {
      state.all[index] = payload;
    }
  },
  REMOVE_MODEL(state: CollectState, payload: number) {
    state.all = state.all.filter((model: Collect) => model.id !== payload);
  },
};

const resource: string = 'collects';

const actions: ActionTree<CollectState, RootState> = {
  clearViewing({ commit }) {
    commit('SET_VIEWING', undefined);
  },
  create({ commit, rootState }, { data, workspaceId }: { data: CollectData; workspaceId: number }) {
    return rootState.api
      .post(`${resource}`, data, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: Collect }) => {
        commit('ADD_MODEL', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  index(
    { commit, rootState },
    { query = { s: { ordinal: SortOrder.ASC, created_at: SortOrder.DESC, id: SortOrder.ASC }, per_page: 20 }, workspaceId }: { query?: IndexQuery<Collect>; workspaceId: number },
  ) {
    const queryString = qs.stringify(query, { encode: false });

    return rootState.api
      .get(`${resource}?${queryString}`, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: Collect }) => {
        if (query.hasOwnProperty('after')) {
          commit('ADD_MODELS', response.data);
        } else {
          commit('SET_MODELS', response.data);
        }
        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  read({ commit, rootState }, { id, workspaceId }: { id: number; workspaceId: number }) {
    return rootState.api
      .get(`${resource}/${id}`, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: Collect }) => {
        commit('UPDATE_MODEL', response.data);

        commit('SET_VIEWING', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  update({ commit, rootState }, { id, data, workspaceId }: { id: number; data: CollectData; workspaceId: number }) {
    return rootState.api
      .put(`${resource}/${id}`, data, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: Collect }) => {
        commit('UPDATE_MODEL', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  patch({ commit, rootState }, { id, data, workspaceId }: { id: number; data: Partial<CollectData>; workspaceId: number }) {
    return rootState.api
      .patch(`${resource}/${id}`, data, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: Collect }) => {
        commit('UPDATE_MODEL', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  delete({ commit, rootState }, { id, workspaceId }: { id: number; workspaceId: number }) {
    return rootState.api
      .delete(`${resource}/${id}`, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: Collect }) => {
        commit('REMOVE_MODEL', id);

        return Promise.resolve(id);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  bulk({ commit, rootState }, { data, workspaceId }: { data: CollectBulkData; workspaceId: number }) {
    return rootState.api
      .post(`${resource}/bulk`, data, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: Collect[] }) => {
        data.collects.forEach((collectData) => {
          if (collectData.action === BulkAction.DELETE) {
            commit('REMOVE_MODEL', collectData.id);
          }
          if (collectData.action === BulkAction.UPDATE || collectData.action === BulkAction.PATCH) {
            const updatedCollect = response.data.find((collect) => collect.id === collectData.id);
            commit('UPDATE_MODEL', updatedCollect);
          }
          if (collectData.action === BulkAction.CREATE) {
            const createdCollect = response.data.find((collect) => collect.product_id === collectData.product_id && collect.collection_id === collectData.collection_id);
            commit('ADD_MODEL', createdCollect);
          }
        });

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  reset({ commit, rootState }) {
    commit('RESET');
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
